import { FC, useMemo, useState } from "react";
import {
	Box,
	Grid,
	LinearProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useUserStore } from "../../../../../stores/user.store";
import useEducationalEngagementReport from "../../../../../hooks/useEducationalEngagementReport";
import BasicKPIComponent from "../../../../Dashboard/BasicKPIComponent";
import DataTable from "../../../../../components/DataTable/DataTable";
import {
	DataTableCell,
	HeadCell,
} from "../../../../../components/DataTable/dataTable.interface";
import ReportFiltersBar, {
	ReportFilters,
	toApiDate,
} from "../../components/ReportFiltersBar";
import { useParticipantColumn } from "../../useParticipantColumn";
import AccessedBadge from "./AccessedBadge";
import AccessRateChart from "./AccessRateChart";

interface Filters extends ReportFilters {
	pageNumber: number;
	pageSize: number;
}

interface EducationalEngagementTabProps {
	programId: string;
}

const EducationalEngagementTab: FC<EducationalEngagementTabProps> = ({
	programId,
}) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const participant = useParticipantColumn();

	const [filters, setFilters] = useState<Filters>({
		orgUnitId: "",
		researcherId: "",
		startDate: null,
		endDate: null,
		pageNumber: 0,
		pageSize: 10,
	});

	const { data, isLoading, isFetching } = useEducationalEngagementReport({
		accountId,
		programId,
		startDate: toApiDate(filters.startDate),
		endDate: toApiDate(filters.endDate),
		pageNumber: filters.pageNumber,
		pageSize: filters.pageSize,
	});

	// Empty-state when the program has no educational content configured.
	const hasNoContent = data && data.kpis.contentItems === 0;

	// Build dynamic columns: participant + 1 column per content item.
	const headCells = useMemo<HeadCell[]>(() => {
		const baseCols: HeadCell[] = [
			{
				id: "participantName",
				label: participant.label,
				isSortable: false,
				disablePadding: false,
				// SIMS-8895 (3): stated explicitly, or DataTableHead right-aligns a
				// non-sortable header over its left-aligned data. The item columns
				// below already carry `align: "center"`, so only this one drifted.
				align: "left",
			},
		];
		const itemCols: HeadCell[] = (data?.byItem ?? []).map((item) => ({
			id: `item_${item.itemId}`,
			label: item.title || "(untitled)",
			isSortable: false,
			disablePadding: false,
			align: "center",
		}));
		return [...baseCols, ...itemCols];
	}, [data?.byItem, participant.label]);

	const rows = useMemo<DataTableCell[]>(() => {
		return (data?.byEnrollment ?? []).map((e, idx) => {
			const row: DataTableCell = {
				id: idx,
				// SIMS-8895 (5): name or CIN per the account setting.
				participantName: participant.display(
					e.participantName,
					e.participantCin,
				),
			};
			for (const flag of e.items) {
				row[`item_${flag.itemId}`] = {
					id: `${e.enrollmentId}-${flag.itemId}`,
					node: <AccessedBadge accessed={flag.accessed} />,
				};
			}
			return row;
		});
	}, [data?.byEnrollment, participant]);

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="educational-engagement"
				filters={filters}
				onChange={(next) =>
					setFilters((p) => ({ ...p, ...next, pageNumber: 0 }))
				}
				hiddenFilters={["orgUnit", "researcher"]}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{hasNoContent ? (
				<Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
					<Typography variant="h6" gutterBottom>
						No educational content configured
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Add content items to this program from the Programs settings to
						start measuring engagement.
					</Typography>
				</Paper>
			) : (
				<>
					<Grid container spacing={2}>
						<BasicKPIComponent
							title="Content items"
							content={data?.kpis.contentItems ?? 0}
							footer={{ hidden: true }}
						/>
						<BasicKPIComponent
							title="Total accesses"
							content={data?.kpis.totalAccesses ?? 0}
							footer={{ hidden: true }}
						/>
						<BasicKPIComponent
							title="Avg access rate"
							content={`${(data?.kpis.avgAccessRate ?? 0).toFixed(1)}%`}
							footer={{ hidden: true }}
						/>
					</Grid>

					<AccessRateChart data={data?.byItem ?? []} isLoading={isLoading} />

					<Box>
						<DataTable
							data={rows}
							rowsPerPage={filters.pageSize}
							order="asc"
							orderBy="participantName"
							page={filters.pageNumber}
							headCells={headCells}
							total={data?.pagination.total ?? 0}
							isLoading={isLoading || isFetching}
							messageEmptyData="No enrollments in scope (ACTIVE + COMPLETED)"
							onChangePage={(_event, newPage) => {
								setFilters((p) => ({ ...p, pageNumber: newPage }));
							}}
							onChangeSize={(event) => {
								setFilters((p) => ({
									...p,
									pageNumber: 0,
									pageSize: Number(event.target.value),
								}));
							}}
						/>
					</Box>
				</>
			)}
		</Stack>
	);
};

export default EducationalEngagementTab;
